import React, { CSSProperties, useEffect, useState } from 'react'
import Loader from '../loader'
import './style.scss'

type BackgroundColorThemes =
  | 'blue'
  | 'transparent'
  | 'purple'
  | 'white'
  | 'blue-darker'
  | 'blue-darker-old'
  | 'dark-1'
  | 'black-blue'
  | 'grey'
  | 'yellow'
  | 'blue-gradient'
  | 'grey-dark'
  | 'black'
  | 'red'
  | 'white-shadow'
  | 'grey-shadow'

interface Props {
  scrollToElementId?: string
  scrollTrigger?: boolean
  fixmargin?: boolean
  loading?: boolean | string
  loaderStyle?: object
  loaderTheme?: 'transparent' | 'white-box'
  zIndex?: number
  fullAbsolute?: boolean
  fullFixed?: boolean
  style?: CSSProperties
  className?: string
  toolbarDisablePadding?: boolean
  children?: any
  contentStyle?: CSSProperties
  toolbarClassName?: string
  toolbarHorizontalScroll?: boolean
  toolbarTheme?: BackgroundColorThemes
  toolbarLeft?: React.ReactNode
  toolbarLeftStyle?: object
  toolbarRight?: React.ReactNode
  toolbarRightStyle?: object
  toolbarVisibility?: boolean
  toolbarStyle?: object
  footerVisibility?: boolean
  sideMenuEnabled?: boolean
  sideMenu?: React.ReactNode
  sideBarRight?: React.ReactNode
  sideBarRightEnabled?: boolean
  content?: React.ReactNode
  footer?: React.ReactNode
  contentId?: string
  contentDisablePadding?: boolean
  contentScroll?: boolean
  wrapperId?: string
  contentTheme?: BackgroundColorThemes
  loaderTransparent?: boolean
}

const MainContent = (props: Props) => {
  const toolbarBackgroundClass = props.toolbarTheme ? 's-theme-' + props.toolbarTheme : ''
  const contentThemeClass = props.contentTheme ? 's-theme-' + props.contentTheme : ''

  const scrollableElement = React.useRef<HTMLDivElement>(null)
  const scrollIndicatorElement = React.useRef<HTMLDivElement>(null)
  const [scrollTries, setScrollTries] = useState<number>(-1)

  const wrapperClasses = () => {
    let classes = 'c-content'
    if (props.className) classes += ' ' + props.className
    if (props.fullAbsolute) classes += ' s-full-absolute'
    if (props.fullFixed) classes += ' s-full-fixed'
    return classes
  }

  const toolbarClasses = () => {
    let classes = 'c-content__toolbar '
    if (props.toolbarClassName) classes += props.toolbarClassName + ' '
    classes += props.toolbarDisablePadding ? 's-disable-padding ' : 's-padding '
    if (toolbarBackgroundClass) {
      classes += ' ' + toolbarBackgroundClass
    }
    if (props.toolbarHorizontalScroll) {
      classes += ' ' + 's-horizontal-scroll'
    }
    return classes
  }

  const contentClasses = () => {
    let classes = 'c-content '
    classes += props.contentScroll ? 'c-content__scrollable ' : 'c-content__not_scrollable '
    classes += props.contentDisablePadding ? 's-disable-padding ' : 's-content-padding '
    if (contentThemeClass) {
      classes += ' ' + contentThemeClass
    }
    return classes
  }

  const scrollToElement = (elementId: string | undefined) => {
    setTimeout(() => {
      if (elementId) {
        const element = document.getElementById(elementId)
        if (element && scrollableElement.current) {
          const position = element.offsetTop - 200
          scrollableElement.current.scrollTop = position
          return true
        }
      }
      if (scrollTries < 10) {
        console.log('scroll try')
        setScrollTries(scrollTries + 1)
        setTimeout(() => {}, 500)
      }
    }, 100)
  }

  useEffect(() => {
    scrollToElement(props.scrollToElementId)
  }, [scrollableElement])

  useEffect(() => {
    scrollToElement(props.scrollToElementId)
  }, [props.scrollTrigger])

  useEffect(() => {
    scrollToElement(props.scrollToElementId)
  }, [props.loading])

  useEffect(() => {
    scrollToElement(props.scrollToElementId)
  }, [])

  function handleScroll(event: React.UIEvent<HTMLElement>) {
    if (scrollIndicatorElement.current) {
      const { currentTarget } = event
      const scrollTop = currentTarget.scrollTop
      const height = currentTarget.scrollHeight - currentTarget.clientHeight

      if (scrollTop > 0) {
        scrollIndicatorElement.current.style.width = `${Math.ceil((scrollTop / height) * 100)}%`
      } else {
        scrollIndicatorElement.current.style.width = 0 + '%'
      }
    }
  }

  const toolbarLeftClasses = () => {
    let classes = 'c-content__toolbar__left'
    if (!props.toolbarRight) {
      classes += ' s-width-100'
    }
    return classes
  }

  function sectionStyle() {
    const style: CSSProperties = { position: 'relative', width: '100%', display: 'flex' }
    if (props.toolbarLeft || props.toolbarRight) {
      style.height = 'calc(100% - 60px)'
    }
    if (props.footer) {
      style.height = 'calc(100% - 90px)'
    }

    if (props.sideMenuEnabled) {
      style.width = 'calc(100% - 300px)'
    }
    return style
  }

  return (
    <main style={props.style} className={wrapperClasses()} id={props.wrapperId}>
      <main className="c-content__relative">
        {(props.toolbarLeft || props.toolbarRight) && (
          <header className={toolbarClasses()} style={props.toolbarStyle || {}}>
            {props.toolbarLeft && (
              <section className={toolbarLeftClasses()} style={props.toolbarLeftStyle ? props.toolbarLeftStyle : {}}>
                {props.toolbarLeft}
              </section>
            )}
            {props.toolbarRight && (
              <section className="c-content__toolbar__right" style={props.toolbarRightStyle ? props.toolbarRightStyle : {}}>
                {props.toolbarRight}
              </section>
            )}
          </header>
        )}
        <section style={sectionStyle()} className={props.sideMenuEnabled ? 'c-content__section s-side-menu__active' : 'c-content__section'}>
          {
            <>
              {props.sideMenu ? <div className="c-content__side-menu">{props.sideMenu}</div> : <></>}
              <section
                id={props.contentId}
                ref={scrollableElement}
                onScroll={(event) => handleScroll(event)}
                className={contentClasses()}
                style={props.contentStyle}
              >
                {props.children || props.content}
              </section>
            </>
          }
          {props.loading && <Loader label={props.loading} style={props.loaderStyle} />}
        </section>
        {props.footer && (props.hasOwnProperty('footerVisibility') ? props.footerVisibility : true) && (
          <div className="c-content__footer">{props.footer}</div>
        )}
      </main>
    </main>
  )
}

export default MainContent
